/**
 * TCID50（組織培養感染量50%）の計算
 * 計算式は docs/systems/keisuke/FORMULAS.md を参照
 */

import { KeisukeAdapterError } from '../keisukeAdapterI18n';

/**
 * TCID50計算の結果
 */
export interface TCID50Result {
  tcid50: number;              // TCID50値
  tcid50Log10: number;         // TCID50値（対数スケール）
  confidenceInterval?: {       // 95%信頼区間（簡易版）
    lower: number;
    upper: number;
  };
  method: 'reed-muench' | 'spearman-karber';
}

/**
 * 希釈倍率データ
 */
export interface DilutionData {
  dilution: number;      // 希釈倍率（例: 10, 100, 1000）
  infected: number;      // 感染数
  total: number;         // 総数
  infectionRate: number; // 感染率（0-1の範囲）
}

/**
 * Reed-Muench法によるTCID50の計算
 * 
 * @param data 希釈倍率データの配列（希釈倍率の高い順）
 * @returns TCID50計算の結果
 */
export function reedMuenchTCID50(data: DilutionData[]): TCID50Result {
  if (data.length < 2) {
    throw new KeisukeAdapterError('minTwoPoints');
  }

  // データを希釈倍率の高い順（感染率の低い順）にソート
  const sortedData = [...data].sort((a, b) => b.dilution - a.dilution);

  // 50%を超える最小の感染率（upper）と、50%未満の最大の感染率（lower）を探す
  let upper: DilutionData | null = null;
  let lower: DilutionData | null = null;

  for (const point of sortedData) {
    // 50%を超える最小の感染率を探す（最初に見つかったものが最小）
    if (point.infectionRate > 0.5) {
      if (upper === null || point.infectionRate < upper.infectionRate) {
        upper = point;
      }
    }
    // 50%未満の最大の感染率を探す（最後に見つかったものが最大）
    if (point.infectionRate < 0.5) {
      if (lower === null || point.infectionRate > lower.infectionRate) {
        lower = point;
      }
    }
  }

  if (upper === null || lower === null) {
    throw new KeisukeAdapterError('tcid50BracketRequired');
  }

  if (Math.abs(upper.infectionRate - lower.infectionRate) < 1e-15) {
    throw new KeisukeAdapterError('reedMuenchInterpolationFailed');
  }

  // Reed-Muench法の計算（連続用量の reedMuenchDoseResponse と同型の線形補間）
  const logUpper = Math.log10(upper.dilution);
  const logLower = Math.log10(lower.dilution);
  const prop =
    (0.5 - lower.infectionRate) / (upper.infectionRate - lower.infectionRate);
  const tcid50Log10 = logLower + prop * (logUpper - logLower);
  const tcid50 = Math.pow(10, tcid50Log10);

  // 信頼区間の計算（簡易版：希釈倍率の間隔を使用）
  // 実際の実装では、より正確な信頼区間の計算が必要
  const dilutionInterval = sortedData.length > 1 
    ? Math.log10(sortedData[0].dilution / sortedData[sortedData.length - 1].dilution) / (sortedData.length - 1)
    : 1;
  const ciWidth = dilutionInterval * 0.5;  // 簡易的に希釈倍率間隔の半分を使用

  const confidenceInterval = {
    lower: Math.pow(10, tcid50Log10 - ciWidth),
    upper: Math.pow(10, tcid50Log10 + ciWidth),
  };

  return {
    tcid50,
    tcid50Log10,
    confidenceInterval,
    method: 'reed-muench',
  };
}

/**
 * Spearman-Karber法によるTCID50の計算
 *
 * 希釈が対数スケールで等間隔であることを前提とする。
 * log10(TCID50) = log10(D_min) + δ × Σ p_i − δ/2
 * ここで D_min は最小希釈倍率（最も濃い段）、δ は隣接段の log10 希釈の差、
 * p_i は各段の感染率の総和（Finney / 公衆衛生の定式に準拠）。
 * 標準的な定義 μ = Σ midpoint × (p_i − p_{i+1}) と等価で、x_min を基準にすると
 * Σ p の符号は正になる（最も濃い段を起点に希釈が進むほど感染率が下がるため）。
 */
export function spearmanKarberTCID50(data: DilutionData[]): TCID50Result {
  if (data.length < 2) {
    throw new KeisukeAdapterError('minTwoPoints');
  }

  const ascending = [...data].sort((a, b) => a.dilution - b.dilution);
  const logD = ascending.map((d) => Math.log10(d.dilution));
  const deltas: number[] = [];
  for (let i = 1; i < logD.length; i += 1) {
    deltas.push((logD[i] ?? 0) - (logD[i - 1] ?? 0));
  }
  const dMean = deltas.reduce((s, x) => s + x, 0) / deltas.length;
  const tol = 1e-6 * Math.max(1, Math.abs(dMean));
  for (const d of deltas) {
    if (Math.abs(d - dMean) > tol) {
      throw new KeisukeAdapterError('spearmanKarberEqualLogDilution');
    }
  }

  const xLow = Math.log10(ascending[0]!.dilution);
  let sumP = 0;
  for (let i = ascending.length - 1; i >= 0; i -= 1) {
    const rate = ascending[i]!.infectionRate;
    if (!Number.isFinite(rate) || rate < 0 || rate > 1) {
      throw new KeisukeAdapterError('infectionRateOutOfRange');
    }
    sumP += rate;
  }

  const tcid50Log10 = xLow + dMean * sumP - dMean / 2;
  const tcid50 = Math.pow(10, tcid50Log10);

  const ciWidth = Math.abs(dMean) * 0.5;
  const confidenceInterval = {
    lower: Math.pow(10, tcid50Log10 - ciWidth),
    upper: Math.pow(10, tcid50Log10 + ciWidth),
  };

  return {
    tcid50,
    tcid50Log10,
    confidenceInterval,
    method: 'spearman-karber',
  };
}

